import { VoidFunctionComponent, useCallback, useEffect, useState, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Group, MathUtils } from 'three';
import { useAccount } from 'wagmi';
import { Text } from '@react-three/drei';
import { Screen } from '@/components/Screen';
import { RecaptchaResult, useOnlybotsBackend } from '@/hooks/useOnlybotsBackend';
import { Helix } from '@/components/Helix';
import { useResponsive } from '@/hooks/useResponsive';
import { monaco } from '@/lib/fonts';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import blipSound from '@/sounds/ob-sfx-processing-blip.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { useWalletContext } from '@/context/WalletContext';

const STATUS_MESSAGES = [
    'determining botness',
    'performing node heuristics',
    'OK',
    'applying recaptcha inversion',
    'OK',
    'blast processing',
    'SYNCHRONIZED',
    'quantum fold',
    'STABLE',
    'coalescence',
    'COMPLETE',
];

const threeGroup = new Group();

interface Props {
    onNotAllowed: () => void;
    onError: () => void;
    next: () => void;
}

export const PerformRecaptcha: VoidFunctionComponent<Props> = ({ onNotAllowed, next, onError }) => {
    const { address } = useAccount();
    const [copyIndex, setCopyIndex] = useState<number>(0);
    const { signature, signedAddress } = useWalletContext();
    const { fetchRecaptchaAssessment } = useOnlybotsBackend();
    const [result, setResult] = useState<RecaptchaResult | null>(null);
    const { responsiveValue } = useResponsive();
    const helix = useRef<Group>(threeGroup);
    const mouse = useLerpedMouse();
    const [playBlipSound] = useOnlybotsSound(blipSound);

    const updateCopy = useCallback(() => {
        setTimeout(() => {
            playBlipSound();
            setCopyIndex(copyIndex + 1);
        }, Math.random() * (1000 - 250) + 250);
    }, [playBlipSound, copyIndex]);

    const performReCaptchaVerification = useCallback(async () => {
        const recaptchaResult = await fetchRecaptchaAssessment();
        setResult(recaptchaResult);
    }, [fetchRecaptchaAssessment, setResult]);

    useEffect(() => {
        if (signature && address === signedAddress) {
            setResult('ALLOWED');
        } else {
            void performReCaptchaVerification();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (copyIndex + 1 < STATUS_MESSAGES.length) {
            updateCopy();
        } else if (result && copyIndex + 1 === STATUS_MESSAGES.length) {
            setTimeout(() => {
                if (result === 'ALLOWED') {
                    next();
                } else if (result === 'NOT_ALLOWED') {
                    onNotAllowed();
                } else {
                    onError();
                }
            }, 500);
        }
    }, [copyIndex, result, updateCopy, next, onNotAllowed, onError]);

    useFrame(() => {
        if (helix.current) {
            helix.current.position.set(MathUtils.lerp(0, 1.0, mouse.current.x), 0, 0);
        }
    });

    return (
        <>
            <Screen>
                <group ref={helix}>
                    <Helix />
                </group>
                <Text
                    font={monaco}
                    textAlign="center"
                    fontSize={responsiveValue(0.14, 0.2)}
                    color="white"
                    maxWidth={responsiveValue(8, 3.5)}
                    lineHeight={1}
                >
                    {STATUS_MESSAGES[copyIndex]}
                </Text>
            </Screen>
        </>
    );
};
